import { FC, useMemo, useRef, useState, useEffect } from 'react'
import { CompositionCareProps } from './types'
import { Link, Text, Image, LazyVideo } from '@lounge-fe/ui-kit'
import snarkdown from 'snarkdown'
import { useInView } from 'framer-motion'

const CompositionCare: FC<CompositionCareProps> = ({ compositionCare }) => {
  const image = compositionCare.media?.image
  const video = compositionCare.media?.video

  const description = useMemo(() => {
    return snarkdown(compositionCare.description)
  }, [compositionCare.description])

  const ref = useRef(null)
  const inView = useInView(ref, { amount: 0 })

  const [hasImageLoaded, setHasImageLoaded] = useState(false)

  useEffect(() => {
    if (inView) {
      setHasImageLoaded(true)
    }
  }, [inView])

  const shouldLoadImage = hasImageLoaded || inView

  return (
    <div className="composition-care bg-primary">
      <div
        className="composition-care__wrapper flex flex-col justify-center items-center py-10 max-w-[calc(100%_-_64px)] sm:max-w-[70vw] 2xl:max-w-[61vw] m-auto md:py-20"
        ref={ref}
      >
        {compositionCare.header && (
          <div className="composition-care__header mb-6 md:mb-12">
            <Text variant="title-five">{compositionCare.header}</Text>
          </div>
        )}
        <div className="composition-care__main flex flex-col items-center gap-10 w-full md:flex-row md:gap-0">
          <div className="composition-care__media basis-1/2 aspect-[4/5] w-full h-full">
            {image && shouldLoadImage && (
              <Image
                data={{
                  src: image.src,
                  width: 628,
                  height: 784,
                }}
                loading="lazy"
                alt={image.alt}
                className="object-cover w-full h-full"
              />
            )}
            {video && <LazyVideo video={video} inView={inView} />}
          </div>
          <div className="composition-care__content basis-1/2 flex flex-col w-full items-start md:pl-[7.5vw]">
            {compositionCare.title && (
              <Text variant="title-five" className="mb-4">
                {compositionCare.title}
              </Text>
            )}
            {compositionCare.description && (
              <Text
                as="div"
                variant="body-sm"
                className="[&_ul]:ml-6 [&_li]:typography--body-sm [&_p]:typography--body-sm"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
            {compositionCare.cta && (
              <Link href={compositionCare.cta?.url}>
                <Text variant="body-md" className="underline mt-4 sm:mt-6">
                  {compositionCare.cta?.text}
                </Text>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export { CompositionCare }
